import { CloseButton } from "@headlessui/react";
import { Trans } from "@lingui/macro";
import { Alert } from "@winamp/ui/components/Alert/Alert.js";
import { AlertActions } from "@winamp/ui/components/Alert/AlertActions.js";
import { AlertDescription } from "@winamp/ui/components/Alert/AlertDescription.js";
import { AlertTitle } from "@winamp/ui/components/Alert/AlertTitle.js";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { useForceUpdate } from "@winamp/ui/utils/useForceUpdate.js";
import {
  Suspense,
  createContext,
  lazy,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  type LinkProps,
  type Location,
  type Path,
  useLocation,
} from "react-router";
import * as v from "valibot";
import { PermissionGuard } from "../permissions/PermissionGuard";
import { getSearchParams } from "../support/url";
import type {
  DialogKey,
  DialogProps,
  DialogSchema,
  PermissionViewProps,
} from "./dialogs";
import { routeDialogs } from "./dialogs";

export const hashParamsSeparator: "-" | "_" | "." | "~" = "~";

interface IRouteDialogContextProviderProps {
  children: React.ReactNode;
}

type IRouteDialogContext = {
  routeDialogs: Array<DialogKey>;
  openedRouteDialogs: Record<DialogKey, { params: URLSearchParams }>;
};

export const RouteDialogContext = createContext<
  IRouteDialogContext | undefined
>(undefined);

export function RouteDialogContextProvider({
  children,
}: IRouteDialogContextProviderProps) {
  const location = useLocation();
  const forceUpdate = useForceUpdate();

  // The dialog keys that are provided in the URL
  const dialogHashes = extractDialogsFromUrl(location);

  // The dialog keys that are both provided in the URL query string and registered
  // Unregistered dialogs are not considered
  const openedRouteDialogs = dialogHashes.reduce(
    (dialogs, dialogHash) => {
      const { hash, params } = parseDialogHash(dialogHash);

      // There is an issue during the tests where the window.location is empty
      const hashesAreSynced =
        import.meta.env.VITEST ??
        (typeof window === "undefined" ||
          location.hash === window.location.hash);

      if (Object.keys(routeDialogs).includes(hash) && hashesAreSynced) {
        dialogs[hash as DialogKey] = { params };
      }

      return dialogs;
    },
    {} as Record<DialogKey, { params: URLSearchParams }>,
  );

  const context = {
    routeDialogs: Object.keys(routeDialogs) as Array<DialogKey>,
    openedRouteDialogs,
  };

  async function onCloseDialog(_dialogKey: DialogKey, _value: boolean) {
    const exitRoute = getDialogExitRoute();

    let exitUrl = exitRoute.pathname ?? "";
    if (exitRoute.search) {
      exitUrl += exitRoute.search;
    }
    if (exitRoute.hash) {
      exitUrl += exitRoute.hash;
    }

    history.pushState(null, "", exitUrl);
    forceUpdate();
  }

  return (
    <RouteDialogContext.Provider value={context}>
      {children}
      {(Object.keys(routeDialogs) as Array<DialogKey>).map((dialogKey) => {
        const Dialog = routeDialogs[dialogKey];
        const hashParams =
          dialogKey in openedRouteDialogs
            ? openedRouteDialogs[dialogKey].params
            : new URLSearchParams();
        const permissions = "permissions" in Dialog ? Dialog.permissions : [];
        const permissionsContext =
          "permissionsContext" in Dialog
            ? Dialog.permissionsContext(hashParams)
            : { allPermissions: true };

        return (
          <PermissionGuard
            key={dialogKey}
            permissions={permissions}
            context={permissionsContext}
          >
            {({ isAuthorized }) => {
              if (isAuthorized) {
                return (
                  <LazyDialog
                    key={dialogKey}
                    dialogKey={dialogKey}
                    openedRouteDialogs={openedRouteDialogs}
                    viewImport={routeDialogs[dialogKey as DialogKey].View}
                    hashParams={hashParams}
                    onClose={(value) => onCloseDialog(dialogKey, value)}
                  />
                );
              }

              return (
                <DefaultPermissionView
                  open={dialogKey in openedRouteDialogs}
                  hashParams={hashParams}
                  onClose={(value) => onCloseDialog(Dialog.key, value)}
                />
              );
            }}
          </PermissionGuard>
        );
      })}
    </RouteDialogContext.Provider>
  );
}

export function useRouteDialogContext() {
  const context = useContext(RouteDialogContext);

  if (context === undefined) {
    throw new Error(
      "useRouteDialogContext must be used withing an RouteDialogContextProvider",
    );
  }

  return context;
}

interface LazyDialogProps {
  dialogKey: DialogKey;
  openedRouteDialogs: Record<DialogKey, { params: URLSearchParams }>;
  viewImport: () => Promise<{ default: React.ComponentType<DialogProps> }>;
  onClose: (value: boolean) => void;
  hashParams: URLSearchParams;
}

function LazyDialog({
  dialogKey,
  viewImport,
  openedRouteDialogs,
  hashParams,
  onClose,
}: LazyDialogProps) {
  const [LoadedView, setLoadedView] =
    useState<React.ComponentType<DialogProps> | null>(null);
  const [delayedOpen, setDelayedOpen] = useState(false);

  const isOpen = dialogKey in openedRouteDialogs;
  useEffect(() => {
    if (isOpen) {
      if (LoadedView) {
        setDelayedOpen(true);
        return;
      }

      viewImport().then((module) => {
        setLoadedView(lazy(() => Promise.resolve({ default: module.default })));
        setTimeout(() => setDelayedOpen(true), 50);
      });
    }
  }, [viewImport, isOpen]);

  useEffect(() => {
    if (!isOpen) setDelayedOpen(false);
  }, [isOpen]);

  return (
    <Suspense fallback={null}>
      {LoadedView && (
        <LoadedView
          data-dialog-id={dialogKey}
          open={delayedOpen}
          hashParams={hashParams}
          onClose={onClose}
        />
      )}
    </Suspense>
  );
}

function DefaultPermissionView({ hashParams, ...props }: PermissionViewProps) {
  return (
    <Alert {...props}>
      <AlertTitle>
        <Trans>Unauthorized</Trans>
      </AlertTitle>
      <AlertDescription>
        <Trans>You are not authorized to access this page.</Trans>
      </AlertDescription>
      <AlertActions>
        <CloseButton as={Button}>
          <Trans>Close</Trans>
        </CloseButton>
      </AlertActions>
    </Alert>
  );
}

function extractDialogsFromUrl(location: Location) {
  return location?.hash.substring(1).split(",");
}

export function buildDialogHash(
  dialogKey: DialogKey,
  state?: Record<string, string | Array<string> | undefined>,
) {
  const hash = dialogKey;

  if (state) {
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(state)) {
      if (value === undefined) continue;
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v));
      } else {
        params.set(key, value);
      }
    }

    return `${hash}${hashParamsSeparator}${params.toString()}`;
  }

  return hash;
}

export function parseDialogHash(dialogHash: string) {
  const [hash, search = ""] = dialogHash.split(hashParamsSeparator);

  const params = new URLSearchParams(search);

  return {
    hash: hash.replace(/^#/, ""), // Remove leading hash symbol
    params,
  };
}

// This utility type checks if T is “assignable” to an empty object type ({}).
// If so, all properties in T are optional
type IsPartial<T> = object extends T ? true : false;

export function getDialogRoute<
  K extends DialogKey,
  Rest extends {
    state?: DialogSchema<K>;
    to?: LinkProps["to"];
  },
>(
  ...args: Rest["state"] extends undefined
    ? [dialogKey: K, to?: Rest["to"]]
    : IsPartial<Rest["state"]> extends true
      ? [dialogKey: K, state?: Rest["state"], to?: Rest["to"]]
      : [dialogKey: K, state: Rest["state"], to?: Rest["to"]]
): Partial<Path> {
  function assertTo(to: unknown): to is Rest["to"] {
    if (
      to &&
      typeof to === "object" &&
      ("pathname" in to || "search" in to || "hash" in to)
    ) {
      return true;
    }

    return typeof to === "string";
  }

  const [dialogKey, ...otherArgs] = args;
  const state = !assertTo(otherArgs[0]) ? otherArgs[0] : undefined;
  const to = assertTo(otherArgs[0]) ? otherArgs[0] : otherArgs[1];
  const hash = buildDialogHash(dialogKey, state);

  if (typeof to === "string") {
    const url = new URL(to, "http://localhost");

    return {
      pathname: url.pathname,
      search: url.search,
      hash,
    };
  }

  if (!to && typeof window !== "undefined") {
    return {
      pathname: window.location.pathname,
      search: window.location.search,
      hash,
    };
  }

  return {
    ...to,
    hash,
  };
}

/**
 * Removes the dialog hash from the URL and returns to the initial URL
 */
export function getDialogExitRoute(): Partial<Path> {
  if (typeof window !== "undefined") {
    return {
      pathname: location.pathname,
      search: location.search,
      hash: "",
    };
  }

  return {
    hash: "",
  };
}

export const useRouteDialogParams = <
  T extends v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>,
>(
  Schema: T,
  hashParams: URLSearchParams,
  open?: boolean,
) => {
  const validation = v.safeParse(Schema, getSearchParams(hashParams));
  const [params, setParams] = useState<
    v.InferOutput<typeof Schema> | undefined
  >(validation.success ? validation.output : undefined);

  useEffect(() => {
    if (open) {
      setParams(validation.success ? validation.output : undefined);
    }
  }, [open, hashParams.toString()]);

  return params;
};
